import { Component, inject, signal } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import {
  ButtonClickEvent,
  CacBaseDialogComponent,
  formBuilder,
  CacDialogLayoutComponent,
  CacImageUploaderComponent,
  CacFormBuilderComponent,
  CacButtonComponent,
  APIHelper,
} from '@controladad/ng-base';
import { formControl } from '@al00x/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, filter, map, of, take, tap } from 'rxjs';
import { CommonModule } from '@angular/common';
import { BlogCreate, BlogDetails, BlogDraftCreate, BlogEntity, BlogUpdate, TagEntity } from '@models';
import { AttachmentApiService, BlogApiService, FileType, TagApiService, UploadResult } from '../../../services';

export interface BlogDialogData {
  blog?: BlogDetails;
}

export type BlogDialogResult = boolean;

@Component({
  selector: 'feature-blog-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    CacDialogLayoutComponent,
    CacImageUploaderComponent,
    CacFormBuilderComponent,
    CacButtonComponent,
  ],
  templateUrl: './blog-dialog.component.html',
  styleUrl: './blog-dialog.component.scss',
})
export class BlogDialogComponent extends CacBaseDialogComponent<BlogDialogData, BlogDialogResult> {
  private readonly blogApi = inject(BlogApiService);
  private readonly tagApi = inject(TagApiService);
  private readonly attachmentApi = inject(AttachmentApiService);

  content = formControl<string>(null, Validators.required);
  image = formControl<string | UploadResult>(null, Validators.required);

  titleCharCount = signal<number>(0);
  subTitleCharCount = signal<number>(0);

  readonly titleMaxLength = 100;
  readonly subTitleMaxLength = 200;

  getAllTags() {
    return this.tagApi
      .getAll<TagEntity>({
        filters: [
          {
            key: 'type',
            value: 1,
          },
        ],
      })
      .pipe(
        tap((tags: TagEntity[]) => {
          this.allTags$.next(tags);
          this.tagMap.clear();
          tags.forEach((tag: TagEntity) => {
            this.tagMap.set(tag.id, tag.name);
          });
        }),
      );
  }

  allTags$ = new BehaviorSubject<TagEntity[]>([]);
  
  form = formBuilder({
    cols: 3,
    inputs: {
      title: {
        control: formControl<string>(undefined, [Validators.required, Validators.maxLength(this.titleMaxLength)]),
        label: 'Title',
        colspan: 3,
      },
      subTitle: {
        control: formControl<string>(
          undefined,
          [Validators.required, Validators.maxLength(this.subTitleMaxLength)]
        ),
        label: 'Subtitle',
        colspan: 3,
      },
      slug: {
        control: formControl<string>(undefined, Validators.required),
        label: 'Slug',
        colspan: 3,
      },
      tags: {
        control: formControl<number[]>([]),
        type: 'select',
        label: 'Tags',
        multiple: true,
        optional: true,
        colspan: 3,
        items: this.allTags$.pipe(map((tags) => tags.map((v) => ({ value: v.id, label: v.name })))),
      },
      status: {
        control: formControl<boolean>(true, Validators.required),
        colspan: 3,
        label: 'Status',
        type: 'switch',
      },
      showInEnglish: {
        control: formControl<boolean>(true),
        label: 'Show In English',
        type: 'switch',
      },
      showInArabic: {
        control: formControl<boolean>(true),
        label: 'Show In Arabic',
        type: 'switch',
      },
    },
    onInit: () => {
      this.getAllTags().subscribe();
    },
  });

  constructor() {
    super();
    this.form.controls.tags.value$.pipe(takeUntilDestroyed()).subscribe((tags) => {
      if (tags) {
        const stringTags = this.form.controls.tags.value.filter((tag) => typeof tag === 'string') as string[];
        const numericTags = this.form.controls.tags.value.filter((tag) => typeof tag === 'number') as number[];

        if (stringTags.length > 0) {
          const newTagIds: number[] = [];

          const createNextTag = (index: number) => {
            if (index >= stringTags.length) {
              this.form.controls.tags.setValue([...numericTags, ...newTagIds]);
              return;
            }

            this.tagApi.create({ name: stringTags[index], type: 1 }).subscribe(() => {
              this.getAllTags().subscribe((allTags: TagEntity[]) => {
                const createdTag = allTags.find((tag: TagEntity) => tag.name === stringTags[index]);
                if (createdTag) {
                  newTagIds.push(createdTag.id);
                }

                createNextTag(index + 1);
              });
            });
          };

          createNextTag(0);
        }
      }
    });

    this.form.controls.subTitle.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      this.titleCharCount.set(value?.length || 0);
      this.subTitleCharCount.set(value?.length || 0);
    });

    const id = this.data?.blog?.id;
    if (id) {
      this.blogApi
        .getBlogDetail(id)
        .pipe(takeUntilDestroyed())
        .subscribe((data: { blog: BlogDetails }) => {
          this.form.patchValue({
            status: data.blog.status === 1,
            slug: data.blog.slug,
            tags: data.blog.tags?.map((tag: any) => tag.id) || [],
            showInEnglish: data.blog.showInEnglish ?? true,
            showInArabic: data.blog.showInArabic ?? true,
            title: data.blog.title,
            subTitle: data.blog.subTitle,
          });
          this.content.patchValue(data.blog.content);
          this.image.patchValue(data.blog.image.url);

          this.titleCharCount.set(data.blog.title?.length || 0);
          this.subTitleCharCount.set(data.blog.subTitle?.length || 0);
        });
    }
  }

  get options() {
    return {
      fileType: 'Blog' as FileType,
    };
  }

  onSubmit(e: ButtonClickEvent) {
    const values = this.form.getValue();
    if (!values) return;

    const id = this.data.blog?.id;
    const imagePath = this.getImagePath();

    const createModel: BlogCreate = {
      ...values,
      title: values.title,
      description: values.subTitle, // Map subTitle to description for API
      status: values.status ? 1 : 0,
      imagePath,
      content: this.content.value,
      tagIds: values.tags,
      showInEnglish: values.showInEnglish,
      showInArabic: values.showInArabic,
    };

    const rawUpdateModel = {
      id: id!,
      imagePath: imagePath,
      status: values.status ? 1 : 0,
      title: values.title,
      description: values.subTitle, // Map subTitle to description for API
      slug: values.slug,
      content: this.content.value,
      tagIds: values.tags,
      showInEnglish: values.showInEnglish,
      showInArabic: values.showInArabic,
    };

    const updateModel: BlogUpdate = APIHelper.nestValues(rawUpdateModel);

    (id ? this.blogApi.updateBlog(updateModel) : this.blogApi.create<BlogEntity>(createModel)).pipe(e.pipe()).subscribe(() => {
      this.submit(true);
    });
  }

  saveDraft(e: ButtonClickEvent) {
    const values = this.form.getValue();
    if (!values) return;

    const id = this.data.blog?.id;
    const imagePath = this.getImagePath();

    if (!id) {
      const createModel: BlogCreate = {
        ...values,
        title: values.title,
        description: values.subTitle, // Map subTitle to description for API
        status: values.status ? 1 : 0,
        imagePath,
        content: this.content.value,
        tagIds: values.tags,
        showInEnglish: values.showInEnglish,
        showInArabic: values.showInArabic,
      };

      this.blogApi.create<BlogEntity>(createModel).pipe(e.pipe()).subscribe((result: BlogEntity) => {
        if (result && result.id) {
          const draftModel: BlogDraftCreate = {
            id: result.id,
            title: values.title,
            description: values.subTitle, // Map subTitle to description for API
            slug: values.slug,
            status: values.status ? 1 : 0,
            imagePath,
            content: this.content.value,
            tagIds: values.tags,
            showInEnglish: values.showInEnglish,
            showInArabic: values.showInArabic,
          };

          this.blogApi.saveDraft(draftModel).subscribe(() => {
            this.submit(true);
          });
        } else {
          this.submit(true);
        }
      });
    } else {
      const draftModel: BlogDraftCreate = {
        id: id,
        title: values.title,
        description: values.subTitle, // Map subTitle to description for API
        slug: values.slug,
        status: values.status ? 1 : 0,
        imagePath,
        content: this.content.value,
        tagIds: values.tags,
        showInEnglish: values.showInEnglish,
        showInArabic: values.showInArabic,
      };

      this.blogApi.saveDraft(draftModel).pipe(e.pipe()).subscribe(() => {
        this.submit(true);
      });
    }
  }

  restorePreviousVersion(e: ButtonClickEvent) {
    const id = this.data.blog?.id;
    if (!id) return;

    this.blogApi.deleteDraft(id).pipe(e.pipe()).subscribe(() => {
      this.submit(true);
    });
  }
  getImagePath(): string {
    const imageValue = this.image.value;
    const imagePath = (typeof imageValue === 'string' ? this.data.blog?.image?.path : imageValue?.imagePath) ?? null;
    return imagePath!;
  }

  convertFieldToValueForm<T>(value: T): any {
    return {
      value: value,
    };
  }

  openEditor() {
    dialog$
      .editor(this.content.value)
      .afterSubmit()
      .pipe(take(1))
      .subscribe((result) => {
        this.content.patchValue(result);
      });
  }

  private tagMap = new Map<number, string>();

  getTagName(tagId: number): string {
    if (this.tagMap.has(tagId)) {
      return this.tagMap.get(tagId) || '';
    }

    return 'Loading...';
  }

  removeTag(tagId: number): void {
    const currentTags = this.form.controls.tags.value || [];
    const updatedTags = currentTags.filter((id) => id !== tagId);
    this.form.controls.tags.setValue(updatedTags);
  }
}
